import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../components/CustomTableWithPagination/GlobalTable';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import FromAndToDatePicker from '../../../components/mui/FromAndToDatePicker';
import useResponsive from '../../../Hooks/useResponsive';
import { skillxStudentsTableHead } from '../../../components/datagrid/skillx/skillxStudentsTableHead';
import { getSkillxStudents, getSkillxStudentsSummary } from '../../../redux/features/skillxSlice';
import { SX } from '../leaderboard/leaderboardData';
import UpdateSkillxId from './UpdateSkillxId';
import BulkUploadSkillxIds from './BulkUploadSkillxIds';
import StatCard from '../SkillxStatCard';

/**
 * IntervalSkillX — Students.
 *
 * Every learner on a SkillX course, and the SkillX ID they have been given.
 *
 * HOW A "SkillX STUDENT" IS FOUND. Nothing on `student` says so. The server
 * joins student → course_enrollment → course_available and keeps the rows whose
 * course belongs to the SkillX project (project_id 8) — the same test every
 * other SkillX-scoped screen in the ERP applies. One row per student, not per
 * enrolment: a learner on both the online and the offline course would
 * otherwise appear twice, offering two places to type one ID into.
 *
 * THE ID IS THE WHOLE POINT. It is issued outside this ERP and entered here by
 * hand — one student from the row's action, or a sheet of admission no → ID
 * pairs through BulkUploadSkillxIds — which is why the "Not linked" pill is the working list and the figure
 * above it is how much of it is left.
 *
 * The date filter acts on the ENROLMENT date — when the student joined a SkillX
 * course. It is the only date in this join that means anything to the person
 * reading the screen.
 */

export default function SkillxStudents() {
  const dispatch = useDispatch();
  const mdDown = useResponsive('down', 'md');

  // Open on everything. A student who enrolled two years ago still needs an ID,
  // so defaulting to this month would hide most of the work this screen is for.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [linked, setLinked] = useState('');

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  // Which row's dialog is open. `null` closes it — the row is also what the form
  // reads, so it is one piece of state rather than an open flag plus a row.
  const [editingRow, setEditingRow] = useState(null);

  const { students } = useSelector((state) => state.skillx);
  const { data, meta, summary, loading } = students;

  const dispatchparams = useMemo(
    () => ({
      paginationData: {
        pageNum: tableState.page,
        gridSize: tableState.pageSize,
        search: tableState.search,
      },
      fromDate,
      toDate,
      linked,
    }),
    [tableState.page, tableState.pageSize, tableState.search, fromDate, toDate, linked],
  );

  useEffect(() => {
    dispatch(getSkillxStudents(dispatchparams));
    dispatch(getSkillxStudentsSummary({ fromDate, toDate }));
  }, [dispatchparams, fromDate, toDate, dispatch]);

  // Changing a filter while sitting on page 6 would ask for a page the narrowed
  // list does not have, and the table would come back empty for no visible
  // reason. Same reason GlobalTable's own search and page-size reset it.
  const resetToFirstPage = useCallback(() => {
    setTableState((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, []);

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (actionKey === 'SET_SKILLX_ID' && row) setEditingRow(row);
  }, []);

  const filters = [
    { key: '', label: 'All', count: summary.total },
    { key: 'no', label: 'Not linked', count: summary.unlinked },
    { key: 'yes', label: 'Linked', count: summary.linked },
  ];

  return (
    <GlobalWrapper title="SkillX Students">
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={4}>
          <StatCard
            icon="solar:users-group-rounded-bold-duotone"
            value={summary.total}
            label="SkillX students"
            color={SX.blue}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <StatCard
            icon="solar:link-round-bold-duotone"
            value={summary.linked}
            label="With a SkillX ID"
            color="#2E7D62"
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <StatCard
            icon="solar:link-broken-bold-duotone"
            value={summary.unlinked}
            label="Still to be linked"
            color="#C25E00"
          />
        </Grid>
      </Grid>

      <Stack gap={2}>
        <Box sx={{ display: 'flex', flexDirection: mdDown ? 'column' : 'row', gap: 2 }}>
          <Box sx={{ flex: 0.5 }}>
            <FromAndToDatePicker
              fullWidth
              disabled={false}
              fromLabel="Enrolled From"
              toLabel="Enrolled To"
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={(d) => {
                setFromDate(d);
                resetToFirstPage();
              }}
              setToDate={(d) => {
                setToDate(d);
                resetToFirstPage();
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: mdDown ? 'flex-start' : 'flex-end',
            }}
          >
            <BulkUploadSkillxIds dispatchparams={dispatchparams} />
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {filters.map((f) => (
            <Chip
              key={f.key || 'all'}
              label={`${f.label} (${f.count ?? 0})`}
              onClick={() => {
                setLinked(f.key);
                resetToFirstPage();
              }}
              color={linked === f.key ? 'primary' : 'default'}
              variant={linked === f.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
          ))}
          {(fromDate || toDate) && (
            <Chip
              label="Clear dates"
              onClick={() => {
                setFromDate('');
                setToDate('');
                resetToFirstPage();
              }}
              variant="outlined"
              icon={<Icon icon="solar:close-circle-bold-duotone" width={16} height={16} />}
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>

        <GlobalTable
          headers={skillxStudentsTableHead}
          rows={data || []}
          meta={meta}
          rowUniqueId="admn_no"
          loading={loading}
          tableState={tableState}
          setTableState={setTableState}
          onButtonActionClick={handleButtonActionClick}
          handleRefresh={() => dispatch(getSkillxStudents(dispatchparams))}
          // Export the whole filtered list, not the page. `limit: 'no'` is the
          // repo's unpaged read — a capped export of a 191-row list would look
          // just as complete as a real one.
          exportApi={getSkillxStudents}
          exportParams={{
            ...dispatchparams,
            paginationData: { ...dispatchparams.paginationData, pageNum: 1, gridSize: 'no' },
          }}
        />
      </Stack>

      <Typography sx={{ mt: 1, fontSize: 12.5, color: SX.textMuted }}>
        A student appears once however many SkillX courses they have taken. The date filter is
        on the enrolment date; both dates have to be set for it to apply.
      </Typography>

      <GlobalDialog
        title={`${editingRow?.skillx_id ? 'Update' : 'Add'} SkillX ID — ${
          editingRow?.student_name || 'Student'
        }`}
        open={Boolean(editingRow)}
        setOpen={(val) => {
          if (!val) setEditingRow(null);
        }}
        closeButton
        fullWidth
        maxWidth="sm"
        content={
          editingRow ? (
            <UpdateSkillxId
              row={editingRow}
              dispatchparams={dispatchparams}
              onClose={() => setEditingRow(null)}
            />
          ) : null
        }
      />
    </GlobalWrapper>
  );
}
